import { HttpEventType } from '@angular/common/http';
import { IDroppedFile, IFileTypeConfig } from '../au-file-loader.interfaces';
import { Subject } from 'rxjs';

export enum FileFormatContentTypeMap {
	PDF = 'application/pdf',
	DOC = 'application/msword',
	DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	XLS = 'application/vnd.ms-excel',
	XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	PPT = 'application/vnd.ms-powerpoint',
	PPTX = 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	JPEG = 'image/jpeg',
	PNG = 'image/png',
	GIF = 'image/gif',
	WEBP = 'image/webp',
	TXT = 'text/plain',
	CSV = 'text/csv',
}

export class FileUtilities {
	fileTypesConfig: IFileTypeConfig;

	constructor(fileTypesConfig: IFileTypeConfig) {
		this.fileTypesConfig = fileTypesConfig;
	}
	static progressEvent(data): boolean {
		return data.event.type === HttpEventType.UploadProgress;
	}

	static finishEvent(data: { event: { type: HttpEventType } }): boolean {
		// HttpEventType.Response means sever returns full response body and transfer ends
		return data.event.type === HttpEventType.Response;
	}

	static getProgressInPercentage(data: { event: any; index: number }): number {
		return Math.round((100 * data.event.loaded) / data.event.total);
	}

	getFileType(name: string, contentType?: string): string {
		const extension = name?.split('.').slice(-1)[0].toLocaleLowerCase();
		const type = contentType?.split('/').slice(-1)[0];

		let fileFormat: string;
		// firstly we are trying to find format by name extension like .doc .jpeg => image
		if (extension) {
			Object.keys(this.fileTypesConfig).forEach(key => {
				if (this.fileFormatSupported(key, extension)) {
					fileFormat = key;
				}
			});
		}
		// if we failed with first stage
		// we are trying to find format by name content type - like application/pdf => document
		if (!fileFormat && type) {
			Object.keys(this.fileTypesConfig).forEach(key => {
				if (this.fileTypesConfig[key].supportedFormatsList.includes(type)) {
					fileFormat = key;
				}
			});
		}
		// if we failed both stages return 'unknownFormat'
		return fileFormat || 'file-unknown';
	}

	static getFilesFromEvent(event: Event): FileList | File[] {
		let target;
		if (event.target) {
			target = event.target as HTMLInputElement;
		} else {
			target = event;
		}

		return (target?.files as FileList) || [];
	}

	static makeObjectForExistingFile(fileData): Partial<IDroppedFile> {
		const { file } = fileData;
		const { contentType, url, pk } = file || fileData;

		return {
			name: fileData.name,
			contentType: contentType,
			loadingProgress: 100,
			url: url,
			pk: pk || fileData.pk,
			deletionLoader: new Subject<boolean>(),
			created: fileData.created,
			createdBy: fileData.createdBy,
			type: fileData.type,
			classifications: fileData.classifications,
			classificationsV2: fileData.classificationsV2,
		};
	}

	static makeObjectForNewFile(file: File, errors): Partial<IDroppedFile> {
		return {
			name: file.name,
			contentType: file.type,
			loadingProgress: 0,
			requestBrakeSubject: new Subject<void>(),
			deletionLoader: new Subject<boolean>(),
			errors,
		};
	}

	static sortFilesByDate(a: Partial<IDroppedFile>, b: Partial<IDroppedFile>) {
		return new Date(b.created).getTime() - new Date(a.created).getTime();
	}

	fileFormatSupported(key, extension): boolean {
		return this.fileTypesConfig[key].supportedFormatsList.includes(extension);
	}

	static passOnlyNewFiles(newFile: { pk: string }, filesList): boolean {
		const index = filesList.findIndex(
			fileFromFileList => fileFromFileList.pk === newFile.pk
		);

		return index === -1;
	}
}
